import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  assistantService,
  type AssistantCopilotAction,
  type AssistantGrounding,
  type AssistantMessage,
  type AssistantPanelContext,
  type AssistantReference,
} from "@/services/modules/assistant";
import { useAIPanel } from "@/contexts/AIPanelContext";
import { cn } from "@/lib/utils";
import { Send, Bot, ExternalLink, Sparkles, RotateCcw, X } from "lucide-react";
import { ContextSnapshot } from "./ai-panel/ContextSnapshot";
import { ChatMessage } from "./ai-panel/ChatMessage";
import { StreamingBubble } from "./ai-panel/StreamingBubble";
import { WelcomeMessage } from "./ai-panel/WelcomeMessage";
import { PinnedItemsBar } from "./ai-panel/PinnedItemsBar";

type Props = {
  moduleContext: string;
  userName: string;
};

// Quick prompts are fallback shortcuts; primary CTA should come from "Explicar na IA" actions in each page.
const CHIPS_BY_MODULE: Record<string, string[]> = {
  geral: ["Prazos críticos de hoje", "Pipeline comercial", "Relatório semanal", "Alertas urgentes"],
  marcas: ["Exigências pendentes", "Oposições ativas", "Vencimentos esta semana", "Resumo do portfólio"],
  patentes: ["Anuidades a vencer", "Exigências técnicas", "Status dos pedidos", "Colidências detectadas"],
  di: ["Exigências DI", "Depósitos recentes", "Prazos de resposta"],
  internacional: ["PCTs em fase nacional", "Protocolo Madrid", "Vencimentos internacionais"],
};

const MODULE_LABELS: Record<string, string> = {
  geral: "Geral",
  comercial: "NIP CRM",
  marcas: "NIP TM",
  patentes: "NIP Pat",
  di: "NIP DI",
  internacional: "Internacional",
};

type StoredPanelSession = {
  messages: AssistantMessage[];
  draft: string;
  references: AssistantReference[];
};

const PANEL_SESSION_STORAGE_PREFIX = "nipsys:ai-panel:session:";

function getPanelSessionKey(moduleContext: string) {
  return `${PANEL_SESSION_STORAGE_PREFIX}${moduleContext}`;
}

function loadPanelSession(moduleContext: string): StoredPanelSession {
  if (typeof window === "undefined") {
    return { messages: [], draft: "", references: [] };
  }

  try {
    const raw = window.sessionStorage.getItem(getPanelSessionKey(moduleContext));
    if (!raw) {
      return { messages: [], draft: "", references: [] };
    }

    const parsed = JSON.parse(raw) as Partial<StoredPanelSession>;
    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      draft: typeof parsed.draft === "string" ? parsed.draft : "",
      references: Array.isArray(parsed.references) ? parsed.references : [],
    };
  } catch {
    return { messages: [], draft: "", references: [] };
  }
}

function savePanelSession(moduleContext: string, session: StoredPanelSession) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(getPanelSessionKey(moduleContext), JSON.stringify(session));
  } catch {
    // Ignore storage failures; chat still works in-memory.
  }
}

function clearPanelSession(moduleContext: string) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(getPanelSessionKey(moduleContext));
  } catch {
    // Ignore storage failures; clearing state is best-effort.
  }
}

export function AIPanelLive({ moduleContext, userName }: Props) {
  const navigate = useNavigate();
  const { panelState, closePanel, consumePendingRequest, pinnedItems } = useAIPanel();
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streamingGrounding, setStreamingGrounding] = useState<AssistantGrounding | undefined>(undefined);
  const [references, setReferences] = useState<AssistantReference[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activePanelContext, setActivePanelContext] = useState<AssistantPanelContext | null>(null);
  const msgsRef = useRef<HTMLDivElement>(null);
  const streamingTextRef = useRef("");
  const inputRef = useRef<HTMLInputElement>(null);
  const hydratedModuleRef = useRef<string | null>(null);
  const moduleLabel = MODULE_LABELS[moduleContext] ?? "NIPSYS";

  const scrollBottom = () => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  };
  useEffect(() => scrollBottom(), [messages, streamingText]);

  // Intercept clicks on internal links rendered by the AI assistant
  useEffect(() => {
    const container = msgsRef.current;
    if (!container) return;
    const handler = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest("a[data-internal-link]") as HTMLAnchorElement | null;
      if (anchor) {
        e.preventDefault();
        const href = anchor.getAttribute("href");
        if (href) navigate(href);
      }
    };
    container.addEventListener("click", handler);
    return () => container.removeEventListener("click", handler);
  }, [navigate]);

  const chips = CHIPS_BY_MODULE[moduleContext] ?? CHIPS_BY_MODULE.geral;

  const send = useCallback(
    async (
      text: string,
      options?: {
        mode?: "chat" | "copilot";
        panelContext?: AssistantPanelContext;
      }
    ) => {
      const resolvedPrompt = text.trim() || options?.panelContext?.prompt?.trim() || "";
      if (!resolvedPrompt || streaming) return;

      const effectivePanelContext: AssistantPanelContext = {
        ...options?.panelContext,
        selectedItems: options?.panelContext?.selectedItems || (pinnedItems.length > 0 ? pinnedItems : undefined),
      };

      const userMsg: AssistantMessage = {
        role: "user",
        content: resolvedPrompt,
      };
      const newMessages = [...messages, userMsg];
      setMessages(newMessages);
      setInput("");
      setStreaming(true);
      setStreamingText("");
      setStreamingGrounding(undefined);
      streamingTextRef.current = "";
      setError(null);
      setReferences([]);
      setActivePanelContext(effectivePanelContext);

      await assistantService.streamChat({
        messages: newMessages,
        moduleContext,
        mode: options?.mode,
        panelContext: effectivePanelContext,
        onChunk: (partial) => {
          streamingTextRef.current = partial;
          setStreamingText(partial);
        },
        onMetadata: (refs, grounding) => {
          setReferences(refs);
          setStreamingGrounding(grounding);
        },
        onDone: (payload) => {
          const finalText = payload.markdown || streamingTextRef.current;
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: finalText,
              grounding: payload.grounding || streamingGrounding,
              viewModel: payload.viewModel,
            },
          ]);
          setStreamingText("");
          setStreamingGrounding(undefined);
          streamingTextRef.current = "";
          setStreaming(false);
          setReferences(payload.references ?? []);
        },
        onError: (err) => {
          setError(err);
          setStreaming(false);
        },
      });
    },
    [messages, moduleContext, streaming, streamingGrounding]
  );

  const handleCopilotAction = useCallback(
    (action: AssistantCopilotAction) => {
      if (action.type === "navigate" && action.route) {
        navigate(action.route);
        return;
      }

      if (action.query) {
        void send(action.query, { mode: action.mode || "chat", panelContext: activePanelContext || undefined });
      }
    },
    [activePanelContext, navigate, send]
  );

  const resetConversation = useCallback(() => {
    setMessages([]);
    setInput("");
    setReferences([]);
    setError(null);
    setStreaming(false);
    setStreamingText("");
    setStreamingGrounding(undefined);
    setActivePanelContext(null);
    streamingTextRef.current = "";
    clearPanelSession(moduleContext);
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [moduleContext]);

  useEffect(() => {
    const stored = loadPanelSession(moduleContext);
    setMessages(stored.messages);
    setInput(stored.draft);
    setReferences(stored.references);
    setError(null);
    setStreaming(false);
    setStreamingText("");
    setStreamingGrounding(undefined);
    setActivePanelContext(null);
    streamingTextRef.current = "";
    hydratedModuleRef.current = moduleContext;
  }, [moduleContext]);

  useEffect(() => {
    if (hydratedModuleRef.current !== moduleContext) {
      return;
    }

    savePanelSession(moduleContext, { messages, draft: input, references });
  }, [input, messages, moduleContext, references]);

  useEffect(() => {
    if (!panelState.open || panelState.pendingRequest) {
      return;
    }

    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => window.clearTimeout(focusTimer);
  }, [panelState.open, panelState.pendingRequest]);

  useEffect(() => {
    if (!panelState.open || !panelState.pendingRequest) {
      return;
    }

    const queuedRequest = consumePendingRequest();
    if (!queuedRequest) {
      return;
    }

    const sendTimer = window.setTimeout(() => {
      void send(queuedRequest.prompt || "", {
        mode: queuedRequest.mode,
        panelContext: queuedRequest.panelContext,
      });
    }, 120);

    return () => window.clearTimeout(sendTimer);
  }, [consumePendingRequest, panelState.open, panelState.pendingRequest, send]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const initials = userName ? userName.substring(0, 2).toUpperCase() : "US";

  return (
    <div className="ai-panel hidden lg:flex flex-col">
      {/* Header */}
      <div className="ai-panel-hdr">
        <div className="min-w-0">
          <div className="ai-panel-title">
            <div className="ai-icon">
              <Bot className="w-3.5 h-3.5 text-white" />
            </div>
            Copiloto NIP
          </div>
          <div className="mt-1 text-[10px] font-medium text-slate-500 truncate">
            {moduleLabel} · leitura operacional · tenant filtrado
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetConversation}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            title="Limpar conversa deste módulo"
            disabled={streaming || (messages.length === 0 && !input.trim())}
          >
            <RotateCcw className="w-3 h-3" />
            Nova
          </button>
          <span
            className={cn(
              "text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full",
              streaming ? "bg-amber-100 text-amber-700 animate-pulse" : "bg-emerald-100 text-emerald-700"
            )}
          >
            {streaming ? "Processando..." : "Online"}
          </span>
          <button
            onClick={closePanel}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
            title="Fechar painel"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Quick Chips */}
      <div className="ai-chips-wrap flex-col items-stretch">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Intenções rápidas
          </div>
          <div className="text-[10px] text-slate-400">
            {messages.length > 0 ? `${messages.length} eventos` : "sem histórico"}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {chips.map((chip) => (
            <button
              key={chip}
              className="ai-chip flex items-center gap-1"
              onClick={() => send(chip)}
              disabled={streaming}
            >
              <Sparkles className="w-2.5 h-2.5 opacity-60" />
              {chip}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div ref={msgsRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        <ContextSnapshot panelContext={activePanelContext} />

        {/* Welcome message if empty */}
        {messages.length === 0 && !streaming && (
          <WelcomeMessage userName={userName} />
        )}

        {/* Chat history */}
        {messages.map((msg, i) => (
          <ChatMessage
            key={i}
            message={msg}
            userInitials={initials}
            onAction={handleCopilotAction}
          />
        ))}

        {/* Streaming bubble */}
        {streaming && (
          <StreamingBubble
            streamingText={streamingText}
            streamingGrounding={streamingGrounding}
          />
        )}

        {/* References */}
        {references.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {references.map((ref) => (
              <a
                key={ref.id}
                href={ref.route || "#"}
                data-internal-link={ref.route?.startsWith("/") ? "true" : undefined}
                className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-100 transition-colors"
              >
                <ExternalLink className="w-2.5 h-2.5" />
                {ref.title}
              </a>
            ))}
          </div>
        )}

        {/* Error notice */}
        {error && (
          <div className="text-[9px] text-amber-600 bg-amber-50 rounded-md px-2 py-1 text-center border border-amber-200">
            ⚡ {error}
          </div>
        )}
      </div>

      {/* Pinned Screen Items Bar */}
      <PinnedItemsBar />

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-slate-200 p-3 flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              closePanel();
            }
          }}
          placeholder={streaming ? "Aguarde a resposta..." : "Pergunte sobre processos, prazos, clientes..."}
          disabled={streaming}
          className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-[12px] outline-none focus:border-blue-400 transition-colors disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          className="w-9 h-9 rounded-lg bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors disabled:opacity-30"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </form>
    </div>
  );
}
